import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChatStore } from '../store/chat';
import { createApiClient } from '@n8n-builder/api-client';
import { API_BASE_URL } from '../lib/api-config';

function MoreIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v.01M12 12v.01M12 19v.01" />
    </svg>
  );
}

function PencilIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
    </svg>
  );
}

function SpinnerIcon({ className }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

export function ConversationSidebar() {
  const navigate = useNavigate();
  const {
    conversations,
    conversationId,
    isLoadingConversations,
    loadingConversationId,
    removeConversation,
    updateConversationInList,
    loadConversationFast,
    setLoadingConversationId,
    startNewConversation,
    fetchConversations,
  } = useChatStore();

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const getApiClient = () => {
    return createApiClient(API_BASE_URL);
  };

  // Load conversations on mount (cache-aware)
  useEffect(() => {
    fetchConversations();
  }, []);

  const handleSelectConversation = async (id: string) => {
    if (id === conversationId) return;

    // Set loading state and navigate immediately
    setLoadingConversationId(id);
    navigate(`/workflow-builder/${id}`);

    try {
      const apiClient = getApiClient();
      const result = await apiClient.getConversationFull(id);

      // Race condition guard: user may have clicked another conversation
      if (useChatStore.getState().loadingConversationId !== id) return;

      if (result.data) {
        loadConversationFast(result.data);
      } else {
        setLoadingConversationId(null);
      }
    } catch (error) {
      console.error('Failed to load conversation:', error);
      setLoadingConversationId(null);
    }
  };

  const handleDeleteConversation = async (id: string) => {
    if (deletingId) return;

    setOpenMenuId(null);
    setDeletingId(id);
    try {
      const apiClient = getApiClient();
      const result = await apiClient.deleteConversation(id);

      if (result.data?.success) {
        removeConversation(id);
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    } finally {
      setDeletingId(null);
    }
  };

  const handleStartRename = (id: string, currentTitle: string) => {
    setOpenMenuId(null);
    setEditingId(id);
    setEditValue(currentTitle);
    setTimeout(() => inputRef.current?.select(), 0);
  };

  const handleRename = async (id: string) => {
    const trimmedValue = editValue.trim();
    if (!trimmedValue) {
      setEditingId(null);
      return;
    }

    try {
      const apiClient = getApiClient();
      const result = await apiClient.updateConversationTitle(id, trimmedValue);

      if (result.data) {
        updateConversationInList(id, { title: trimmedValue });
      }
    } catch (error) {
      console.error('Failed to rename conversation:', error);
    } finally {
      setEditingId(null);
    }
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent, id: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleRename(id);
    } else if (e.key === 'Escape') {
      setEditingId(null);
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };

    if (openMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openMenuId]);

  return (
    <div className="flex h-full flex-col pt-1">
      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto px-2">
        {isLoadingConversations ? (
          <div className="space-y-2 px-1">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-11 rounded-lg skeleton"
              />
            ))}
          </div>
        ) : conversations.length === 0 ? (
          <div className="px-1">
            <button
              onClick={() => {
                startNewConversation();
                navigate('/workflow-builder');
              }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"
            >
              <PlusIcon className="h-4 w-4" />
              New Workflow
            </button>
          </div>
        ) : (
          <ul className="space-y-1">
            {conversations.map((conv) => {
              const isActive = conversationId === conv.id;
              const isLoadingThis = loadingConversationId === conv.id;
              const isDeleting = deletingId === conv.id;
              const isEditing = editingId === conv.id;
              const isMenuOpen = openMenuId === conv.id;

              return (
                <li key={conv.id} className="relative">
                  <button
                    onClick={() => !isEditing && handleSelectConversation(conv.id)}
                    className={`group relative flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm transition-all duration-200 ${
                      isActive || isLoadingThis
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {isEditing ? (
                      <input
                        ref={inputRef}
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={() => handleRename(conv.id)}
                        onKeyDown={(e) => handleRenameKeyDown(e, conv.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="flex-1 rounded border border-primary-300 bg-white px-2 py-0.5 text-sm font-medium text-gray-900 outline-none focus:ring-2 focus:ring-primary-500"
                        autoFocus
                      />
                    ) : (
                      <span className="flex-1 truncate font-medium pr-6">{conv.title}</span>
                    )}

                    {/* More Button / Spinner */}
                    {!isEditing && (
                      isLoadingThis ? (
                        <span className="absolute right-2 p-1">
                          <SpinnerIcon className="h-4 w-4 text-primary-500" />
                        </span>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuId(isMenuOpen ? null : conv.id);
                          }}
                          disabled={isDeleting}
                          className={`absolute right-2 rounded-md p-1 transition-all duration-200 ${
                            isDeleting || isMenuOpen
                              ? 'opacity-100'
                              : 'opacity-0 group-hover:opacity-100'
                          } ${
                            isActive
                              ? 'hover:bg-primary-100'
                              : 'hover:bg-gray-200'
                          }`}
                          title="More options"
                        >
                          {isDeleting ? (
                            <SpinnerIcon className="h-4 w-4 text-gray-400" />
                          ) : (
                            <MoreIcon className="h-4 w-4 text-gray-500" />
                          )}
                        </button>
                      )
                    )}
                  </button>

                  {/* Dropdown Menu */}
                  {isMenuOpen && (
                    <div
                      ref={menuRef}
                      className="absolute right-0 top-full z-50 mt-1 w-36 rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
                    >
                      <button
                        onClick={() => handleStartRename(conv.id, conv.title)}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <PencilIcon className="h-4 w-4" />
                        Rename
                      </button>
                      <button
                        onClick={() => handleDeleteConversation(conv.id)}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                      >
                        <TrashIcon className="h-4 w-4" />
                        Delete
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
